import { useEffect, useState } from 'react'
import type * as Y from 'yjs'
import type { CitationField } from './collectClusters.js'
import { type SectionCitations, createScopeCitations } from './sectionCitations.js'

export type { SectionCitations } from './sectionCitations.js'

const DELAY_MS = 150

/** De secties van één nummering: één laag, in formuliervolgorde, met hun rich-text-velden. */
export type CitationScope = readonly { readonly sectieId: string; readonly fieldIds: readonly CitationField[] }[]

/**
 * Eén citatieberekening per laag, over de secties van die laag die de lezer heeft (R-50, eisenboek
 * 12.9), naar CitatenPOC's `useFormCitations` met zijn `ResultsContext`.
 *
 * **Over het voorstel, niet per sectie.** R-50 vult de referentielijst "uit de verwijzingen in de
 * tekst van het voorstel", en CitatenPOC rekent over het hele formulier. De documenten blijven per
 * sectie (B6): dit leest ze naast elkaar (`citationsOf`) en voegt niets samen.
 *
 * **Per laag.** De toetslaag heeft een eigen nummering: een bron van de toetser hoort niet in de
 * literatuurlijst van de aanvraag, en mag de letters daar niet veranderen.
 *
 * **Alleen wat de lezer heeft.** De aanroeper geeft de secties uit zijn projectie, met de velden die
 * hij ziet. Een sectie die hij niet ziet telt niet mee; anders zou een "2026b" verraden dat er een
 * tweede bron van die auteur is die hij niet te zien krijgt. Wie het hele formulier ziet, ziet
 * daarmee precies wat de revisie vastlegt (`exportScopeSources` in de pod); wie minder ziet, kan een
 * letter missen die de revisie wel draagt.
 *
 * **Eén berekening voor de labels en voor de lijst**, vertraagd (150 ms, zoals CitatenPOC), en alleen
 * een nieuwe uitkomst als er iets veranderde. De uitkomst staat per sectie-id, met hetzelfde object
 * voor elke sectie van een laag. De kaart en de lijst moeten stabiel zijn tussen renders.
 */
export function useSectionCitations(
  documents: ReadonlyMap<string, Y.Doc>,
  scopes: readonly CitationScope[],
): ReadonlyMap<string, SectionCitations> {
  const [results, setResults] = useState<ReadonlyMap<string, SectionCitations>>(new Map())

  useEffect(() => {
    const stops: (() => void)[] = []
    const active = new Set<string>()
    for (const scope of scopes) {
      const sections = scope.flatMap(({ sectieId, fieldIds }) => {
        const doc = documents.get(sectieId)
        return doc === undefined || fieldIds.length === 0 ? [] : [{ sectieId, doc, fieldIds }]
      })
      if (sections.length === 0) continue
      // Ook een sectie zonder eigen rich text krijgt de uitkomst: daar staat de referentielijst in
      // extract 213, bij de bijlagen.
      const members = scope.map(({ sectieId }) => sectieId)
      for (const sectieId of members) active.add(sectieId)
      const compute = createScopeCitations()
      let timer: ReturnType<typeof setTimeout> | undefined
      const recompute = (): void => {
        if (timer !== undefined) clearTimeout(timer)
        timer = setTimeout(() => {
          const next: SectionCitations = compute(sections)
          setResults((previous) => {
            const current = previous.get(members[0] ?? '')
            if (current !== undefined && sameOutcome(current, next) && members.every((id) => previous.get(id) === current)) return previous
            const updated = new Map(previous)
            for (const sectieId of members) updated.set(sectieId, next)
            return updated
          })
        }, DELAY_MS)
      }
      recompute()
      for (const { doc } of sections) doc.on('update', recompute)
      stops.push(() => {
        if (timer !== undefined) clearTimeout(timer)
        for (const { doc } of sections) doc.off('update', recompute)
      })
    }
    // Een sectie die er niet meer is (een andere instantie in een reeks) houdt geen oude uitkomst.
    setResults((previous) => {
      if ([...previous.keys()].every((sectionId) => active.has(sectionId))) return previous
      return new Map([...previous].filter(([sectionId]) => active.has(sectionId)))
    })
    return () => {
      for (const stop of stops) stop()
    }
  }, [documents, scopes])

  return results
}

function sameOutcome(a: SectionCitations, b: SectionCitations): boolean {
  if (a.inTekst.size !== b.inTekst.size || a.literatuurlijst.length !== b.literatuurlijst.length) return false
  for (const [citationId, html] of a.inTekst) if (b.inTekst.get(citationId) !== html) return false
  return a.literatuurlijst.every(
    (entry, i) => entry.bronId === b.literatuurlijst[i]?.bronId && entry.html === b.literatuurlijst[i]?.html,
  )
}
